import type { useLoaderData } from '@remix-run/react';
import { useMatches } from '@remix-run/react';
import { useMemo } from 'react';
// see bottom of file for code originally from `tiny-invariant` package
// import invariant from '../tiny-invariant';

type RouteId = string;
type SerializeFrom<T> = ReturnType<typeof useLoaderData<T>>;

export function useRouteData<DataFunction>(routeId: RouteId) {
  return useMatchesData<SerializeFrom<DataFunction>>(routeId, true);
}

export function useOptionalRouteData<DataFunction>(routeId: RouteId) {
  return useMatchesData<SerializeFrom<DataFunction>>(routeId);
}

// overload 1 (like useRouteData)
// isRequired: true -> T
export function useMatchesData<T = Record<string, unknown>>(
  routeId: RouteId,
  isRequired: true
): T;

// overload 2 (like useOptionalRouteData)
// isRequired: false | undefined -> T | undefined
export function useMatchesData<T = Record<string, unknown>>(
  routeId: RouteId,
  isRequired?: false
): T | undefined;

// implementation (like existing useMatchesData)
// isRequired: boolean | undefined (default to false) -> T | undefined
export function useMatchesData<T = Record<string, unknown>>(
  routeId: RouteId,
  isRequired = false
): T | undefined {
  // set all hooks before early returns to avoid rules-of-hooks error re: conditional rendering
  const matchingRoutes = useMatches();
  const routeMatch = useMemo(
    () => matchingRoutes.find((route) => route.id === routeId),
    [matchingRoutes, routeId]
  );

  // the root loader is always the first match in remix
  const rootMatch = matchingRoutes[0];
  const appContext = rootMatch?.data;
  const isMissingContext = typeof appContext === 'undefined';
  if (isMissingContext) {
    if (isRequired) invariant(isMissingContext, 'Missing app context');
    return undefined;
  }

  // early exit if they want `root`'s data since we already have it
  if (routeId === 'root') return appContext as unknown as T;

  return routeMatch?.data as unknown as T;
}

// inlined here until we can find a better place for it
// code originally from `tiny-invariant` package
// this mod/"fork" is to add the `console.error` of the full message in production
const isProduction: boolean = process.env.NODE_ENV === 'production';
const prefix = 'Invariant failed';

// Throw an error if the condition fails
// Strip out error messages for production
// > Not providing an inline default argument for message as the result is smaller
function invariant(
  condition: unknown,
  // Can provide a string, or a function that returns a string for cases where
  // the message takes a fair amount of effort to compute
  message?: string | (() => string)
): asserts condition {
  if (condition) {
    return;
  }
  // Condition not passed

  // When not in production we allow the message to pass through
  const provided: string | undefined =
    typeof message === 'function' ? message() : message;

  // Options:
  // 1. message provided: `${prefix}: ${provided}`
  // 2. message not provided: prefix
  const value: string = provided ? `${prefix}: ${provided}` : prefix;

  // In production, we log the full message but throw a stripped down version
  const fullError = new Error(value);
  if (isProduction) {
    console.error(fullError);
    throw new Error(prefix);
  }

  throw fullError;
}
